import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw, Plus, Pencil, Trash2, Upload, FileText, Eye, AlertTriangle } from "lucide-react";

const CATEGORIES = [
  { value: "checklists",            label: "Checklists" },
  { value: "machtigingen",          label: "Machtigingen" },
  { value: "paklijsten",            label: "Paklijsten" },
  { value: "boekingsformulieren",   label: "Boekingsformulieren" },
  { value: "douane",                label: "Douane / documentatie" },
  { value: "verhuizing",            label: "Verhuizing" },
  { value: "export",                label: "Export" },
  { value: "algemene-voorwaarden",  label: "Algemene voorwaarden" },
];

const EMPTY = {
  title: "", description: "", category: "checklists",
  file_url: "", file_name: "", file_type: "pdf",
  gridfs_id: "", size_bytes: 0,
  active: true, sort_order: 0,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DownloadsTab() {
  const { user } = useAuth();
  // Mirror backend RBAC for write/edit (admin_phase11_documents._require_role)
  // — only owner/admin/manager can mutate. Medewerker may still SEE the
  // section (gedeeld onder Documenten & Formulieren) but won't see Add /
  // Edit / Delete / Toggle / Upload controls.
  const canEdit = ["owner", "admin", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set("category", filter);
      const r = await adminApi.get(`/admin/documents?${params.toString()}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const startCreate = () => {
    setForm(EMPTY);
    setEditing(null);
    setOpen(true);
  };

  const startEdit = (item) => {
    setForm({
      title: item.title || "",
      description: item.description || "",
      category: item.category || "checklists",
      file_url: item.file_url || "",
      file_name: item.file_name || "",
      file_type: item.file_type || "pdf",
      gridfs_id: item.gridfs_id || "",
      size_bytes: item.size_bytes || 0,
      active: !!item.active,
      sort_order: item.sort_order ?? 0,
    });
    setEditing(item);
    setOpen(true);
  };

  const setF = (k) => (e) => {
    const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", f);
      const token = localStorage.getItem("cargo_admin_token");
      const r = await axios.post(`${BACKEND_URL}/api/admin/documents/upload`, data, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setForm((s) => ({
        ...s,
        file_url: r.data.file_url,
        file_name: r.data.file_name || s.file_name || f.name,
        file_type: r.data.file_type || s.file_type || "pdf",
        gridfs_id: r.data.gridfs_id || "",
        size_bytes: r.data.size_bytes || 0,
      }));
      toast.success("Bestand geüpload");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titel is verplicht"); return; }
    // Phase 18.4 — accept either a freshly uploaded gridfs_id OR an
    // external https:// URL pasted into file_url. The backend then sets
    // the row's stable /api/website/documents/{id}/download URL.
    const hasFile = !!(form.gridfs_id || /^https?:\/\//i.test(form.file_url || ""));
    if (!hasFile) { toast.error("Upload een bestand of plak een externe https:// URL"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order) || 0,
        size_bytes: Number(form.size_bytes) || undefined,
        gridfs_id: form.gridfs_id || undefined,
      };
      // For freshly uploaded files we send file_url="" — the backend will
      // set the stable URL itself. Pre-existing edits keep their URL.
      if (form.gridfs_id && !editing) {
        payload.file_url = "";
      }
      if (editing) {
        await adminApi.put(`/admin/documents/${editing.id}`, payload);
        toast.success("Document bijgewerkt");
      } else {
        await adminApi.post("/admin/documents", payload);
        toast.success("Document toegevoegd");
      }
      setOpen(false);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const toggleActive = async (item) => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/documents/${item.id}`, { active: !item.active });
      setItems((arr) => arr.map((x) => x.id === item.id ? { ...x, active: !x.active } : x));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Document "${item.title}" definitief verwijderen?`)) return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/documents/${item.id}`);
      toast.success("Verwijderd");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const displayUrl = (u) => {
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    return `${BACKEND_URL}${u}`;
  };

  const missingCount = items.filter((it) => it.file_missing).length;

  return (
    <div className="space-y-4" data-testid="downloads-tab">
      {missingCount > 0 && (
        <div
          className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm flex items-start gap-3"
          data-testid="downloads-missing-banner"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <b>{missingCount} document{missingCount === 1 ? "" : "en"} ontbreek{missingCount === 1 ? "t" : "en"} op de server.</b>
            <span className="ml-2">
              De publieke pagina toont deze niet meer. Klik op
              {" "}<span className="font-chivo font-bold">"Opnieuw uploaden"</span>{" "}
              bij de betreffende rij om het bestand te herstellen — alle
              bestaande links blijven daarna werken.
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="downloads-filter-category"
          className="rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10"
        >
          <option value="">Alle categorieën</option>
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <Button onClick={refresh} variant="outline" className="rounded-none" data-testid="downloads-refresh">
          <RefreshCw className="w-4 h-4 mr-2" />Verversen
        </Button>
        {canEdit && (
          <Button onClick={startCreate} className="rounded-none ml-auto bg-[#0055FF]" data-testid="downloads-add">
            <Plus className="w-4 h-4 mr-2" />Nieuw document
          </Button>
        )}
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider font-chivo font-bold text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Titel</th>
              <th className="px-3 py-2 text-left">Categorie</th>
              <th className="px-3 py-2 text-left">Bestand</th>
              <th className="px-3 py-2 text-center">Volgorde</th>
              <th className="px-3 py-2 text-center">Actief</th>
              <th className="px-3 py-2 text-right">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Laden…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500">Geen documenten.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="hover:bg-gray-50" data-testid={`downloads-row-${it.id}`}>
                <td className="px-3 py-2">
                  <div className="font-chivo font-bold text-[#0A192F]">{it.title}</div>
                  {it.description && <div className="text-xs text-gray-500 line-clamp-2">{it.description}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">{CATEGORIES.find((c) => c.value === it.category)?.label || it.category}</td>
                <td className="px-3 py-2">
                  {it.file_url || it.download_url ? (
                    <div className="flex items-center gap-1 flex-wrap">
                      {it.file_missing ? (
                        <>
                          <span
                            className="inline-flex items-center gap-1 text-red-700 text-[10px] bg-red-50 border border-red-200 px-1.5 py-0.5 font-chivo font-bold uppercase tracking-wider"
                            title={`Detectie: ${it.file_missing_detected_at || 'eerder'}`}
                            data-testid={`downloads-missing-${it.id}`}
                          >
                            <AlertTriangle className="w-3 h-3" /> Bestand ontbreekt
                          </span>
                          {canEdit && (
                            <Button
                              size="sm"
                              className="rounded-none bg-red-600 hover:bg-red-700 text-white text-[10px] h-6 px-2"
                              onClick={() => startEdit(it)}
                              data-testid={`downloads-restore-${it.id}`}
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              Opnieuw uploaden
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          {it.previewable && it.view_url && (
                            <a
                              href={displayUrl(it.view_url)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[#0A192F] text-xs hover:underline"
                              title="Bekijken"
                              data-testid={`downloads-view-${it.id}`}
                            >
                              <Eye className="w-3 h-3" /> Bekijken
                            </a>
                          )}
                          <a
                            href={displayUrl(it.download_url || it.file_url)}
                            target="_blank"
                            rel="noreferrer"
                            download={it.file_name || undefined}
                            className="inline-flex items-center gap-1 text-[#0055FF] text-xs hover:underline"
                            title="Download"
                            data-testid={`downloads-link-${it.id}`}
                          >
                            <Download className="w-3 h-3" /> {(it.file_type || "pdf").toUpperCase()}
                          </a>
                        </>
                      )}
                    </div>
                  ) : <span className="text-xs text-gray-400">—</span>}
                </td>
                <td className="px-3 py-2 text-center text-xs">{it.sort_order ?? 0}</td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => canEdit && toggleActive(it)}
                    disabled={busy || !canEdit}
                    data-testid={`downloads-toggle-${it.id}`}
                    className={`text-[10px] uppercase tracking-wider font-chivo font-bold px-2 py-1 ${it.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"} ${!canEdit ? "cursor-default opacity-70" : ""}`}
                  >
                    {it.active ? "Actief" : "Inactief"}
                  </button>
                </td>
                <td className="px-3 py-2 text-right space-x-2">
                  {canEdit && (
                    <>
                      <Button size="sm" variant="outline" className="rounded-none" onClick={() => startEdit(it)} data-testid={`downloads-edit-${it.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-none text-red-600" onClick={() => remove(it)} data-testid={`downloads-delete-${it.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="downloads-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Document bewerken" : "Nieuw document"}</DialogTitle>
            <DialogDescription>Upload een PDF/Word/Excel-bestand of plak een externe URL.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Titel *</Label>
                <Input data-testid="downloads-form-title" value={form.title} onChange={setF("title")} className="rounded-none" />
              </div>
              <div className="sm:col-span-2">
                <Label>Omschrijving</Label>
                <Textarea data-testid="downloads-form-desc" rows={2} value={form.description} onChange={setF("description")} className="rounded-none" />
              </div>
              <div>
                <Label>Categorie *</Label>
                <select data-testid="downloads-form-category" value={form.category} onChange={setF("category")} className="w-full rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Sorteervolgorde</Label>
                <Input data-testid="downloads-form-sort" type="number" value={form.sort_order} onChange={setF("sort_order")} className="rounded-none" />
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 p-3 space-y-2">
              <Label className="text-[11px] uppercase tracking-wider font-chivo font-bold text-[#0055FF]">Bestand</Label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={onUpload}
                  className="text-xs"
                  data-testid="downloads-form-file"
                />
                {uploading && <Loader2 className="w-4 h-4 animate-spin text-[#0055FF]" />}
              </div>
              <div className="text-[11px] text-gray-500">Of plak een externe URL (https://…) hieronder.</div>
              <Input data-testid="downloads-form-url" value={form.file_url} onChange={setF("file_url")} className="rounded-none font-mono text-xs" placeholder="/api/static/uploads/documents/…  of  https://…" />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="downloads-form-filename" value={form.file_name} onChange={setF("file_name")} className="rounded-none" placeholder="Bestandsnaam (optioneel)" />
                <Input data-testid="downloads-form-filetype" value={form.file_type} onChange={setF("file_type")} className="rounded-none" placeholder="pdf" />
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={setF("active")} data-testid="downloads-form-active" />
              Actief op publieke pagina
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button onClick={save} disabled={busy || uploading} className="rounded-none bg-[#0055FF]" data-testid="downloads-form-save">
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editing ? "Bewaren" : "Toevoegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
